import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import Loader from "../components/loader.tsx";
import { Box, HStack, Stack, Table } from "@chakra-ui/react";
import { CustomPagination } from "../components/ui/custom-pagination.tsx";
import { Button } from "../components/ui/button.tsx";
import { FaPlus } from "react-icons/fa";
import { Switch } from "../components/ui/switch.tsx";
import { usePagination } from "../hooks";

export const DemandsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const pageSize = 10;

  const {
    pageIndex: pageEntryIndex,
    setPage,
    nextPage: onNextEntryPage,
    previousPage: onPreviousEntryPage,
    canPreviousPage: canPreviousEntryPage,
  } = usePagination({
    pageType: "demands",
    id: id!,
    pageSize,
  });

  const { data: demands, isLoading: isLoadingEntries } = useQuery(
    ["demands", id, pageEntryIndex, pageSize],
    () => api.demand.getDemands(id!, pageSize, pageEntryIndex * pageSize),
    {
      keepPreviousData: false,
    },
  );

  const { mutate } = useMutation({
    mutationFn: (id: string) => api.demand.close(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["demands", id] });
      await queryClient.invalidateQueries({ queryKey: ["codebook"] });
    },
  });

  // Calculate canNextPage based on data
  const canNextEntryPage = demands
    ? pageEntryIndex < Number(demands?.totalPages) - 1
    : false;

  if (isLoadingEntries) {
    return <Loader />;
  }

  return (
    <Box
      w="100%"
      h="full"
      pt="5.25rem"
      p="1rem"
      bg="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <HStack
        w="full"
        justify="space-between"
        pt="5.25rem"
        mb="1rem"
        flexWrap="wrap"
      >
        <Box fontSize={["lg", "xl", "2xl"]} fontWeight="semibold" color="black">
          PREGLED TREBOVANJA
        </Box>
        <Button
          size={"sm"}
          colorPalette={"gray"}
          variant={"outline"}
          width={"10rem"}
          color={"#000000"}
          backgroundColor={"#ffffff"}
          borderColor={"#ffffff"}
          fontWeight={"600"}
          fontSize={"14px"}
          borderRadius={"0.5rem"}
          onClick={() => navigate(`create`)}
        >
          <FaPlus /> Dodaj trebovanje
        </Button>
      </HStack>
      <Stack width="full" gap="5">
        <Box overflowX="auto">
          <Table.Root
            size="sm"
            stickyHeader
            interactive
            variant="outline"
            minWidth="800px"
          >
            <Table.Header bg="gray.200">
              <Table.Row>
                <Table.ColumnHeader
                  key={1}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  KA MAGACINU
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={2}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  KOLICINA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={3}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  ZAVRSENO
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {demands?.items?.map((item) => (
                <Table.Row
                  key={item.id}
                  className="hover:bg-gray-100 cursor-pointer"
                >
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.to}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.quantity}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    <Switch
                      id={item.id}
                      checked={item.closed}
                      onChange={() => mutate(item.id)}
                      disabled={item.closed}
                      className={"mt-[8px]"}
                      colorPalette={"blue"}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <CustomPagination
            count={demands!.totalItems}
            pageSize={10}
            currentPage={pageEntryIndex}
            onPageChange={setPage}
            onNextPage={onNextEntryPage}
            onPreviousPage={onPreviousEntryPage}
            canNextPage={canNextEntryPage}
            canPreviousPage={canPreviousEntryPage}
            siblingCount={1}
          />
        </Box>
      </Stack>
    </Box>
  );
};
